import type { CostPrices, UnitHours } from '../../../services/adminUnits';
import { EstimatePolicy, HoursPerMonth, estimateNextMonth, formatMoney } from './unitCosts';
import { plural } from './unitsFormat';

interface CostEstimateProps {
  hours: UnitHours;
  prices: CostPrices;
  policy: EstimatePolicy;
}

/** Next month's estimate with its parts, priced at the policy as it stands in its controls. */
export default function CostEstimate({ hours, prices, policy }: CostEstimateProps) {
  const estimate = estimateNextMonth(hours, prices, policy);
  const money = (amount: number) => formatMoney(amount, prices.currency);
  return (
    <div className="card" aria-label="Next month estimate" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>Next month, estimated</span>
        <span className="tnum" style={{ fontWeight: 600, fontSize: 16 }}>~{money(estimate.total)}</span>
        {policy.unsaved && <span className="chip warn"><span className="dot" />Unsaved policy values</span>}
      </div>
      <div className="tnum">
        {hours.hours.toFixed(1)} unit-hours in {hours.days} days → ~{money(estimate.usage)}/month
      </div>
      <div className="tnum">
        Warm {policy.warmUnits} × {HoursPerMonth} h → {money(estimate.warm)}
      </div>
      <div className="tnum">Manager {money(estimate.manager)}</div>
      <div className="muted tnum" style={{ fontSize: 12 }}>
        {policy.cores} cores, {policy.memoryGb} GB: {formatMoney(estimate.unitPerHour, prices.currency, 4)}/h
        {estimate.perJob != null && ` · ${plural(hours.jobsDone, 'job', 'jobs')} in ${hours.days} days, ~${money(estimate.perJob)} a job`}
        {' · '}Prices as of {prices.pricesAsOf || 'unknown'}; pulls not counted
      </div>
    </div>
  );
}
